"use client";

import Image from "next/image";
import { use } from "react";
import { IoMdInformationCircleOutline } from "react-icons/io";

import { shimmer } from "@/common/components/shimmer";
import { MigrationLink } from "@/common/components-2/migration-link";
import { toBase64 } from "@/common/helpers";
import { truncate } from "@/common/helpers/string";
import { Price } from "@/common/models/price";
import { CompanyMetaContext } from "@/modules/homepage/api";
import { useTranslations } from "@/services/i18n";
import { goToCatalogPage } from "@/services/navigation";

import { Badge } from "../badge";
import { Button } from "../button";
import { Tooltip, TooltipArrow, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip/tooltip.component";
import {
  getCoverImageClass,
  getCoverImageWrapperClass,
  getNameClass,
  getPackageCardClass,
  getPriceClass,
} from "./package-card.styles";
import type { PackageCardProps } from "./package-card.types";
import { useSubproducts } from "./use-subproducts";

const DESCRIPTION_MAX_LENGTH = 160;
const PACKAGE_NAME_MAX_LENGTH = 56;

export function PackageCard({
  className,
  name,
  category,
  description,
  price,
  bloodTests,
  detailsUrl,
  coverImageUrl,
  ctaText,
  readMoreText,
  ctaVariant = "primary" as const,
  coverSize = "base",
  hideDescription = false,
  ...props
}: PackageCardProps) {
  const {
    translations: { minHomeVisitPrice },
  } = use(CompanyMetaContext);
  const t = useTranslations("ui.packageCard");
  const descriptionText = truncate(description, { length: DESCRIPTION_MAX_LENGTH });
  const { subproducts, restSubProducts } = useSubproducts(bloodTests);
  const packageName = truncate(name, { length: PACKAGE_NAME_MAX_LENGTH });

  const handleOnSelect = () => {
    goToCatalogPage({ product: name });
  };

  return (
    <div className={getPackageCardClass({ className })} {...props}>
      <div className={getCoverImageWrapperClass()}>
        <Badge variant="primary" size="xs" className="absolute top-4 left-2 z-10 lg:text-base">
          {category}
        </Badge>
        <div className={getPriceClass()}>{Price.format(price, "pl")} </div>
        <Badge variant="white" size="xs" className="absolute right-2 bottom-4 z-10 text-primary-500 lg:text-base">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <span className="flex items-center gap-1">
                  <IoMdInformationCircleOutline className="text-xl" />
                  {t("homeVisitText")}
                  {Price.format(Price.from(minHomeVisitPrice), "pl", 0)}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-64 leading-normal">
                {t("service_tooltip_content")}
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Badge>

        <Image
          src={coverImageUrl}
          alt={name}
          width={400}
          height={250}
          className={getCoverImageClass({ coverSize })}
          placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(400, 250))}`}
        />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="flex flex-1 cursor-default select-none flex-col items-center px-2 py-4 text-center">
          <p className={getNameClass()}>{packageName}</p>
          <div className="mb-2 flex gap-2">
            {subproducts.slice(0, 2).map((b) => (
              <Badge key={b} variant="secondary" size="xs" className="lg:text-base">
                {b}
              </Badge>
            ))}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="secondary" size="xs" className="lg:text-base">
                    + {restSubProducts.length} {t("tests")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-64">
                  {restSubProducts.join(", ")}
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {!hideDescription && <p className="px-4">{descriptionText}</p>}
        </div>
        <div className="mb-4 flex items-center justify-center px-6 max-lg:gap-5 lg:justify-between">
          <Button asChild variant="ghost" size="sm">
            <MigrationLink href={detailsUrl} target="_blank">
              {readMoreText ?? t("readMore")}
            </MigrationLink>
          </Button>
          <Button size="sm" variant={ctaVariant} onClick={handleOnSelect}>
            {ctaText ?? t("selectPackage")}
          </Button>
        </div>
      </div>
    </div>
  );
}
